// VOY-485 (rebuild) — My trips (Public profile tab).
//
// Mirrors the live `MyTripsRow` data path: GET /api/v2/profile/:uid/trips
// (enriched server-side per VOY-413). Splits into Upcoming + Past, each
// horizontally scrollable. Cards link to /trips/:orderId.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plane, Building, Package } from 'lucide-react'
import { apiFetch } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { parseTripDate } from '@/lib/dates'
import type { Trip } from '@/types/trip'

export interface MyTripsSectionProps {
  /**
   * The userId whose trips to render. The parent passes this from its
   * loaded `profile.id` so the prop signature matches every other v485
   * data-section. We still read `user.id` from useAuth() as the actual
   * fetch identity — defense-in-depth so a stale prop can never fetch
   * another user's trips, matching the live MyTripsRow pattern.
   */
  userId: string
}

const UPCOMING_STATUSES = new Set<Trip['status']>([
  'upcoming', 'active', 'created', 'holding', 'holding_partial', 'confirming',
])
const PAST_STATUSES = new Set<Trip['status']>(['past'])

function iconFor(trip: Trip) {
  // Trip carries `flights[]` and `hotels[]` from the enriched VOY-413 shape.
  // Both populated → package; otherwise pick the dominant.
  const hasFlights = (trip.flights?.length ?? 0) > 0
  const hasHotels = (trip.hotels?.length ?? 0) > 0
  if (hasFlights && hasHotels) return <Package aria-hidden="true" />
  if (hasFlights) return <Plane aria-hidden="true" />
  if (hasHotels) return <Building aria-hidden="true" />
  return <Package aria-hidden="true" />
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null
  const target = parseTripDate(iso)
  if (!target) return null
  const ms = target.getTime() - Date.now()
  if (ms < 0) return null
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

function TripCard({ trip }: { trip: Trip }) {
  const start = trip.date_range?.start ?? null
  const days = daysUntil(start)
  return (
    <Link to={`/trips/${trip.id}`} className="trip-card">
      <div className="trip-card-icon">{iconFor(trip)}</div>
      <div className="trip-card-body">
        <div className="trip-card-title">{trip.destination || 'Trip'}</div>
        <div className="trip-card-meta">
          {start ? new Date(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
          {days !== null && <span className="trip-card-countdown"> · in {days}d</span>}
        </div>
      </div>
    </Link>
  )
}

export function MyTripsSection({ userId: _userId }: MyTripsSectionProps) {
  // `userId` prop is part of the signature for parity with the other
  // v485 data-sections, but intentionally unused for the fetch — we
  // read `user.id` from `useAuth()` as defense-in-depth so a stale
  // prop can't leak another user's trips (matches live MyTripsRow).
  // The leading underscore on the local marks it explicitly unreferenced.
  const { user } = useAuth()
  const [trips, setTrips] = useState<Trip[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.id) return
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await apiFetch(`/api/v2/profile/${user.id}/trips`)
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || 'Failed to load trips')
        }
        const data = (await res.json()) as Trip[]
        setTrips(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('[MyTripsSection] load failed:', err)
        setError(err instanceof Error ? err.message : 'Failed to load trips')
      } finally {
        setLoading(false)
      }
    })()
  }, [user?.id])

  const upcoming = trips.filter((t) => UPCOMING_STATUSES.has(t.status))
  const past = trips.filter((t) => PAST_STATUSES.has(t.status))

  return (
    <section className="card" aria-labelledby="trips-h">
      <div className="card-h">
        <h3 id="trips-h">My trips</h3>
        <span className="meta">{loading ? 'LOADING…' : `${trips.length} TOTAL`}</span>
      </div>
      {error && (
        <p style={{ color: 'var(--v6-neg)', fontSize: 12, margin: 0 }} role="alert">{error}</p>
      )}
      {!loading && !error && trips.length === 0 && (
        <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
          No trips yet — your bookings show up here once they&apos;re confirmed.
        </p>
      )}
      {upcoming.length > 0 && (
        <div className="trip-group">
          <div className="lbl trip-group-label">UPCOMING</div>
          <div className="trip-row">
            {upcoming.map((t) => <TripCard key={t.id} trip={t} />)}
          </div>
        </div>
      )}
      {past.length > 0 && (
        <div className="trip-group" style={{ marginTop: 14 }}>
          <div className="lbl trip-group-label">PAST</div>
          <div className="trip-row muted">
            {past.map((t) => <TripCard key={t.id} trip={t} />)}
          </div>
        </div>
      )}
    </section>
  )
}
